import axios from "axios";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import queryString from "query-string";
import React, { useState } from "react";
import * as Yup from "yup";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";

import { COLOR_KICK_BLUE } from "~/shared/sharedConstants.js";
import { kickRed } from "~/shared/styles/baseStyles.js";
import { bugsnagClient } from "~/shared/utils/errorHandling";
import { TextMaskPhone } from "~/visits/components/shared.jsx";

const initialValues = {
  passwordResetToken: "",
  password: "",
  email: "",
  phone: "",
  sendMethod: "email",
  givesSMSConsent: true,
};

export default function PortalPasswordReset() {
  const [passwordResetError, setPasswordResetError] = useState("");
  const [passwordResetMessage, setPasswordResetMessage] = useState(<p></p>);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [sendMethod, setSendMethod] = useState("email");
  const [givesSMSConsent, setGivesSMSConsent] = useState(true);

  const parsedQS = queryString.parse(location.search);

  if (parsedQS.token) {
    initialValues.passwordResetToken = parsedQS.token;
  }

  const passwordValidationSchema = Yup.object().shape({
    password: Yup.string()
      .min(6, "Please enter a password with at least 6 characters")
      .required("Password is required"),
  });

  const emailValidationSchema = Yup.object().shape({
    email: Yup.string().when("sendMethod", {
      is: "email",
      then: (schema) => schema.email().required("Email is required"),
    }),
    phone: Yup.string().when("sendMethod", {
      is: "sms",
      then: (schema) => schema.required("Phone number is required"),
    }),
  });

  const renderInputEmailForm = () => {
    return (
      <Formik
        validationSchema={emailValidationSchema}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          axios
            .put("/me/password-reset/", {
              sendMethod: sendMethod,
              email: values.email,
              phone: values.phone,
            })
            .then(() => {
              setSubmitting(false);

              setPasswordResetMessage(
                <p style={{ color: kickRed }}>
                  {sendMethod === "email" ? (
                    <>
                      Email sent. Check your inbox for a reset email. <br />
                      Make sure to check your spam folder too.
                    </>
                  ) : (
                    "Text message sent. Check your phone for a reset message."
                  )}
                </p>,
              );
              setPasswordResetError("");
            })
            .catch((error) => {
              setSubmitting(false);
              console.error("Error resetting password:", error.response);
              bugsnagClient.notify(error);
              setPasswordResetError(error.response.data.errors[0].title);
              setPasswordResetMessage(<p></p>);
            });
        }}
        validate={(values) => {
          console.log("values", values);
          const errors = {};
          if (sendMethod === "sms" && !givesSMSConsent) {
            errors.givesSMSConsent = "You must consent to receive SMS messages";
          }

          return errors;
        }}
      >
        {({ errors, handleChange, setFieldValue }) => (
          <Form
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "30px",
              padding: "10px",
            }}
          >
            <p style={{ marginTop: "20px" }}>
              How would you like to receive your password reset link?
            </p>

            <RadioGroup
              name="sendMethod"
              value={sendMethod}
              onChange={(e) => {
                setSendMethod(e.target.value);
                handleChange(e);
                setFieldValue("email", "");
                setFieldValue("phone", "");
              }}
              style={{ flexDirection: "row", alignSelf: "flex-start" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "20px",
                }}
              >
                <Radio
                  style={{ padding: "4px" }}
                  value="email"
                  checked={sendMethod === "email"}
                />
                <label>Email</label>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Radio
                  style={{ padding: "4px" }}
                  value="sms"
                  checked={sendMethod === "sms"}
                />
                <label>SMS</label>
              </div>
            </RadioGroup>

            {sendMethod === "sms" ? (
              <div style={{ marginTop: "16px" }}>
                <p>What phone number did you use to sign up?</p>
                <Field
                  component={TextField}
                  defaultValue=""
                  id="phone"
                  name="phone"
                  label="Phone"
                  type="phone"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    inputComponent: TextMaskPhone,
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginTop: "10px",
                  }}
                >
                  <FormControlLabel
                    style={{ marginRight: "2px", marginLeft: "0" }}
                    control={
                      <Checkbox
                        checked={givesSMSConsent}
                        onChange={(e) => {
                          setGivesSMSConsent(e.target.checked);
                          handleChange(e);
                        }}
                        style={{ padding: "4px" }}
                      />
                    }
                  />
                  <p style={{ marginBottom: "0" }}>
                    I consent to receive text messages from Kick Health
                  </p>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    color: "red",
                  }}
                >
                  {!givesSMSConsent && errors.givesSMSConsent}
                </div>
              </div>
            ) : (
              <div style={{ marginTop: "16px" }}>
                <p>What email address did you use to sign up?</p>
                <Field
                  component={TextField}
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                />
              </div>
            )}

            <div
              style={{
                textAlign: "center",
                marginTop: "10px",
                color: "red",
              }}
            >
              {passwordResetError}
            </div>
            <div
              style={{
                textAlign: "center",
                marginTop: "10px",
              }}
            >
              {passwordResetMessage}
            </div>

            <Button
              type="submit"
              color="primary"
              size="large"
              style={{
                backgroundColor: "#f25930",
                color: "white",
                width: "100%",
                justifySelf: "center",
                alignSelf: "center",
                display: "flex",
                flex: "1",
                textTransform: "unset",
              }}
            >
              {sendMethod === "email"
                ? "Send Password Reset Email"
                : "Send Password Reset SMS Message"}
            </Button>
          </Form>
        )}
      </Formik>
    );
  };

  const renderNewPasswordForm = () => {
    const passwordResetSuccessMsg = [
      <p key="passwordResetSuccessMsg">
        Password successfully changed.
        <br />
        <a href="/me">Login For Treatments, Refills and Messaging</a>
      </p>,
    ];
    return (
      <Formik
        validationSchema={passwordValidationSchema}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          axios
            .post("/me/password-reset/", {
              passwordResetToken: values.passwordResetToken,
              password: values.password,
            })
            .then(() => {
              setSubmitting(false);
              setPasswordResetSuccess(true);
            })
            .catch((error) => {
              setSubmitting(false);
              console.error("Error resetting password:", error.response);

              setPasswordResetError(error.response.data.errors[0].title);
            });

          // action("submit")(values);
        }}
        render={({ submitForm, isSubmitting }) => (
          <Form
            style={{
              display: "flex",
              maxWidth: "280px",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {!passwordResetSuccess && (
              <React.Fragment>
                <p>Please Enter a New Password</p>
                <Field
                  component={TextField}
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  variant="outlined"
                  style={{ marginTop: "16px" }}
                  fullWidth
                  onKeyPress={(ev) => {
                    if (ev.key === "Enter") {
                      submitForm();
                    }
                  }}
                />

                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    color: "red",
                  }}
                >
                  {passwordResetError}
                </div>
              </React.Fragment>
            )}

            <div style={{ textAlign: "center" }}>
              {passwordResetSuccess ? passwordResetSuccessMsg : null}
            </div>
            {!passwordResetSuccess && (
              <React.Fragment>
                {passwordResetError && (
                  <a style={{ marginTop: "10px" }} href="/me/password-reset">
                    Request New Password Reset Email
                  </a>
                )}

                <Button
                  disabled={isSubmitting}
                  type="submit"
                  color="primary"
                  size="large"
                  style={{
                    backgroundColor: "#f25930",
                    color: "white",
                    marginTop: "10px",
                    justifySelf: "center",
                    alignSelf: "center",
                    display: "flex",
                    flex: "1",
                    textTransform: "unset",
                  }}
                >
                  Update Password
                </Button>
              </React.Fragment>
            )}
          </Form>
        )}
      />
    );
  };
  return (
    <Grid
      container
      justifyContent="center"
      style={{
        backgroundColor: COLOR_KICK_BLUE,
        minHeight: "100vh",
      }}
    >
      <Grid item style={{}} md={8} xs={10}>
        <div
          style={{
            backgroundColor: "white",
            display: "flex",
            justifyContent: "center",
            minHeight: "330px",
            marginTop: "140px",
            alignSelf: "center",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              style={{
                fontFamily: "BasisGrotesquePro-Bold",
                fontSize: "24px",
                textAlign: "center",
                margin: "30px 0 20px 0",
              }}
            >
              Choose New Password
            </Typography>
            {initialValues.passwordResetToken
              ? renderNewPasswordForm()
              : renderInputEmailForm()}
          </div>
        </div>
      </Grid>
    </Grid>
  );
}
